import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/shared/lib/cn";

/**
 * Button — five intents × six sizes. Apple-ish: 1pt borders, no
 * heavy shadows, 200ms ease-out interaction. The "ghost" intent is the
 * everyday "tap and forget" affordance for navigation.
 *
 * `loading` swaps the leading content for a spinning Loader2 and forces
 * the button into disabled state — never use both `loading` and onClick
 * branches in the same handler.
 */
const button = cva(
  [
    "inline-flex items-center justify-center gap-2 select-none whitespace-nowrap",
    "font-medium tracking-[-0.005em]",
    "transition-[background,color,border-color,box-shadow] duration-[var(--motion-base)] ease-[var(--ease-out-soft)]",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-0",
    "disabled:opacity-40 disabled:cursor-not-allowed",
  ],
  {
    variants: {
      intent: {
        primary:
          "bg-[var(--accent-solid)] text-[var(--accent-fg)] hover:bg-[var(--accent-solid-hover)] active:brightness-95",
        secondary:
          "bg-[var(--bg-subtle)] text-[var(--fg)] hover:bg-[var(--bg-active)]",
        ghost:
          "bg-transparent text-[var(--fg)] hover:bg-[var(--bg-hover)]",
        outline:
          "bg-transparent text-[var(--fg)] border border-[var(--border-strong)] hover:bg-[var(--bg-hover)]",
        danger:
          "bg-[var(--danger)] text-white hover:brightness-95 active:brightness-90",
      },
      size: {
        xs:   "h-7  px-2.5 text-[12px] rounded-[var(--radius-sm)]",
        sm:   "h-8  px-3   text-[13px] rounded-[var(--radius-md)]",
        md:   "h-10 px-3.5 text-[14px] rounded-[var(--radius-md)]",
        lg:   "h-11 px-5   text-[15px] rounded-[var(--radius-lg)]",
        xl:   "h-12 px-6   text-[16px] rounded-[var(--radius-lg)]",
        icon: "h-10 w-10 rounded-[var(--radius-md)]",
      },
      block: { true: "w-full", false: "" },
    },
    defaultVariants: { intent: "secondary", size: "md", block: false },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {
  asChild?: boolean;
  loading?: boolean;
  /** Optional leading element when not loading. */
  leading?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, intent, size, block, asChild, loading, leading, disabled, children, ...props },
    ref,
  ) => {
    // asChild 는 자식 하나에 모양만 입힌다 — 앞 아이콘·로딩 자리까지 넘기면 Slot 이 자식 둘을 받아
    // React.Children.only 로 화면이 죽었다(2026-10-01 폰 점검: 없는 현장 /sites/:id 의 [목록으로]).
    if (asChild) {
      return (
        <Slot ref={ref} className={cn(button({ intent, size, block }), className)} aria-busy={loading || undefined} {...props}>
          {children}
        </Slot>
      );
    }
    const Comp = "button";
    return (
      <Comp
        ref={ref}
        className={cn(button({ intent, size, block }), className)}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? (
          <Loader2 size={14} strokeWidth={2} className="animate-spin" aria-hidden />
        ) : (
          leading
        )}
        {children}
      </Comp>
    );
  },
);
Button.displayName = "Button";
